import { Text, makeStyles, tokens } from '@fluentui/react-components';
import type { FieldRow } from './types';

/**
 * Two-column label/value list — used for the Contact card on the client
 * detail page (email / phone / address / owner). Filters out rows whose
 * value is empty/whitespace; renders a single italic dash when nothing
 * is left so the card never collapses to zero height.
 */
const useStyles = makeStyles({
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, max-content) 1fr',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalS,
    alignItems: 'baseline',
  },
  fieldLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  fieldValue: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  emptyHint: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
});

export function FieldList({ rows, dash }: { rows: FieldRow[]; dash: string }) {
  const styles = useStyles();
  const visible = rows.filter(r => r.value && r.value.toString().trim().length > 0);
  if (visible.length === 0) {
    return <Text className={styles.emptyHint}>{dash}</Text>;
  }
  return (
    <div className={styles.fieldGrid}>
      {visible.map(row => (
        <FieldPair key={row.label} label={row.label} value={row.value!} />
      ))}
    </div>
  );
}

function FieldPair({ label, value }: { label: string; value: string }) {
  const styles = useStyles();
  return (
    <>
      <Text className={styles.fieldLabel}>{label}</Text>
      <Text className={styles.fieldValue}>{value}</Text>
    </>
  );
}
